import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/api";
import {
  Calculator, LayoutDashboard, ShieldCheck, LogOut, Menu, X, FileBarChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/calculator", label: "Calculator", icon: Calculator, roles: "all" },
  { to: "/assignments", label: "My Assignments", icon: FileBarChart, roles: "all" },
  { to: "/portfolio", label: "Portfolio", icon: LayoutDashboard, roles: ["hr_business_partner", "mobility_specialist", "comp_admin", "tax_reviewer", "system_administrator", "auditor"] },
  { to: "/admin", label: "Admin Console", icon: ShieldCheck, roles: ["hr_business_partner", "mobility_specialist", "comp_admin", "tax_reviewer", "system_administrator"] },
];

function Brand({ compact }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative">
        <div className="bg-white rounded-md px-1 py-0.5 flex items-center border border-slate-200"><img src="/mesabi-logo.png" alt="Mesabi Metallics" className={compact ? "h-6 w-auto" : "h-7 w-auto"} /></div>
        <span className="absolute -right-0.5 -top-0.5 w-2.5 h-2.5 rounded-full bg-[#C8102E]" style={{ boxShadow: "0 0 8px rgba(200,16,46,0.6)" }} />
      </div>
      <div>
        <div className="font-display font-extrabold tracking-tight leading-none text-slate-900 text-[15px]">Mesabi Metallics</div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-[#D97706] mt-0.5 font-mono-fin">Global Mobility</div>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const canSee = (roles) => roles === "all" || (user && roles.includes(user.role));

  const NavItems = () => (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => canSee(n.roles)).map((n) => (
        <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} onClick={() => setOpen(false)}
          className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}>
          <n.icon className="h-4 w-4" size={16} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="px-5 py-4 border-b border-slate-100"><Brand /></div>
        <div className="px-3 pt-4 pb-1"><div className="term-label px-3 mb-1.5">Navigation</div></div>
        <div className="flex-1"><NavItems /></div>
        <div className="p-3 border-t border-slate-100">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 mb-2">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[10px] text-[#C8102E] font-mono-fin uppercase tracking-wider mt-0.5">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          <Button data-testid="logout-btn" variant="ghost"
            className="w-full justify-start text-slate-500 hover:bg-slate-100 hover:text-slate-900 h-9 rounded-lg"
            onClick={async () => { await logout(); nav("/login"); }}>
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="hidden lg:flex sticky top-0 z-30 h-12 items-center justify-between px-6 bg-white/85 backdrop-blur border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <span className="ticker-dot" />
            <span className="term-label text-slate-500">Live · Balance-Sheet Engine v2.0</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="term-label">Ref Q1 2026</span>
            <span className="term-label text-slate-400">USD Base</span>
            <span className="hidden xl:flex items-center gap-2 text-xs text-slate-600"><span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" />{user?.name}</span>
          </div>
        </header>

        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
          <Brand compact />
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="text-slate-600">{open ? <X /> : <Menu />}</button>
        </header>
        {open && (
          <div className="lg:hidden bg-white border-b border-slate-200 py-3">
            <NavItems />
            <div className="px-4 pt-3 mt-2 border-t border-slate-100">
              <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
              <div className="text-[10px] text-[#C8102E] font-mono-fin uppercase tracking-wider mb-2">{ROLE_LABELS[user?.role]}</div>
              <Button variant="ghost" className="text-slate-500 px-0" onClick={async () => { await logout(); nav("/login"); }}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            </div>
          </div>
        )}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
